import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { environment } from 'src/environments/environment';
import { allowVideoUrl, isAssetStreamUrl } from '../../constants';
import { CommonHttpService } from '../../services/common-http/common-http.service';
import { ElectronService } from '../../services/electron/electron.service';

/**
 * Where to park the playhead so the browser paints a still frame as a preview.
 * Frame 0 is often black, so nudge just past it.
 */
const PREVIEW_FRAME_TIME = 0.1;

@Component({
    selector: 'app-vplaybook-video-viewer',
    templateUrl: './vplaybook-video-viewer.component.html',
    styleUrls: ['./vplaybook-video-viewer.component.scss'],
    standalone: false
})
export class VplaybookVideoViewerComponent implements AfterViewInit, OnDestroy {

  primaryUrl: string;
  videoSrc: string;
  isPlaying: boolean;
  offlineAppPath: any;
  onErrorCalled = 0;
  onErrorFuncAllowed = true;

  /** Tracks the in-flight play() so pause() can never race it. */
  private pendingPlay: Promise<void> | null = null;
  private viewReady = false;
  private previewSeekDone = false;

  @ViewChild('videoPlayer') videoPlayer: ElementRef<HTMLVideoElement>;
  @Input() videoAssetId: string;
  /**
   * When true the element shows an early frame as a still preview. This is a
   * metadata load plus a seek -- it never plays and never produces audio.
   */
  @Input() isLoadVideo: boolean = true;
  @Input() videoClass: string = 'wh-100';
  @Input() isTimeUpdate: boolean = false;
  @Input() onErrorRetry: number = 3;
  @Input() isEmbedThirdPartyUrl: boolean = false;
  /** Optional real poster image; when supplied no preview seek is needed. */
  @Input() posterUrl: string;
  @Input() public set setVideoSrc(videoSrc: string) {
    this.videoSrc = videoSrc;
    this.fetchVideoUrl();
  }
  @Input() public set setIsPlaying(isPlaying: boolean) {
    this.isPlaying = isPlaying;
    this.applyPlayState();
  }

  @Output() onTimeUpdate: EventEmitter<any> = new EventEmitter();
  @Output() onAddVideoComment: EventEmitter<any> = new EventEmitter();
  @Output() downloadAsset: EventEmitter<any> = new EventEmitter();
  /** Fires when playback actually starts, including via the native controls. */
  @Output() playbackStarted: EventEmitter<void> = new EventEmitter();

  constructor(
    private commonHttpService: CommonHttpService,
    private electronService: ElectronService
  ) {
    this.offlineAppPath = this.electronService.appPath;
  }

  /**
   * Only fetch metadata when we need a frame to show. With no preview to
   * paint we download nothing until the user presses play.
   */
  get preloadMode(): string {
    return this.needsPreviewSeek ? 'metadata' : 'none';
  }

  private get needsPreviewSeek(): boolean {
    return !!this.isLoadVideo && !this.posterUrl;
  }

  private get media(): HTMLVideoElement | null {
    return this.videoPlayer?.nativeElement ?? null;
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    const media = this.media;
    if (media) {
      // Paint the preview frame once metadata for the current source lands.
      media.addEventListener('loadedmetadata', this.onLoadedMetadata);
    }
    // setIsPlaying() may have been applied before the ViewChild resolved.
    this.applyPlayState();
  }

  /**
   * Seek to an early frame so the element renders a still image. Runs once per
   * source, and never while the user is actually watching.
   */
  private onLoadedMetadata = (): void => {
    const media = this.media;
    if (!media || this.isPlaying || this.previewSeekDone || !this.needsPreviewSeek) {
      return;
    }
    if (media.duration > PREVIEW_FRAME_TIME) {
      this.previewSeekDone = true;
      media.currentTime = PREVIEW_FRAME_TIME;
    }
  };

  /**
   * play()/pause() have to be serialized: pausing while a play() promise is
   * still pending rejects it with AbortError, and Safari has historically
   * ignored the pause outright and kept the video running. So we always let a
   * pending play settle before pausing, and we swallow the abort.
   */
  private safePlay(): void {
    const media = this.media;
    if (!media) {
      return;
    }
    const started = media.play();
    if (started && typeof started.then === 'function') {
      this.pendingPlay = started
        .catch(() => { /* autoplay blocked, or superseded by a pause */ })
        .then(() => { this.pendingPlay = null; });
    }
  }

  private safePause(): void {
    const media = this.media;
    if (!media) {
      return;
    }
    if (this.pendingPlay) {
      this.pendingPlay.then(() => this.media?.pause());
      return;
    }
    media.pause();
  }

  /**
   * Drive the element from `isPlaying` only. Nothing else in this component
   * may start playback.
   */
  private applyPlayState(): void {
    const media = this.media;
    if (!this.viewReady || !media) {
      return;
    }
    if (this.isPlaying) {
      this.safePlay();
    } else if (this.pendingPlay || !media.paused) {
      // Nothing to do for an element that is already idle -- but a pending
      // play() still has to be cancelled even though `paused` reads true.
      this.safePause();
    }
  }

  /** Kept for template/API compatibility with existing call sites. */
  playVideo(): void {
    this.applyPlayState();
  }

  /**
   * The user can start playback straight from the native controls, so mirror
   * the element's real state rather than trusting the last @Input value.
   */
  onMediaPlay(): void {
    this.isPlaying = true;
    this.playbackStarted.emit();
  }

  onMediaPause(): void {
    this.isPlaying = false;
  }

  fetchVideoUrl(isCurrentTime = false) {
    let currentTime;
    let wasPlaying = false;
    if (isCurrentTime && this.media) {
      currentTime = this.media.currentTime;
      wasPlaying = !!this.isPlaying;
      this.onErrorCalled++;
      if (this.onErrorCalled === this.onErrorRetry) {
        this.onErrorFuncAllowed = false;
      }
    }
    if (this.videoSrc.includes('data')
    || this.videoSrc.includes(this.offlineAppPath) 
    || this.videoSrc.includes(environment.saasTokenDomain)
    || this.videoSrc.includes(allowVideoUrl)
    || isAssetStreamUrl(this.videoSrc)
    || this.isEmbedThirdPartyUrl) {
      this.setSource(this.videoSrc);
    } else {
      this.commonHttpService.get<any>({
        uri: this.videoSrc,
        message: {
          showMessage: false
        }
      }).subscribe((videoDataUrl) => {
        if (isCurrentTime && this.media) {
          this.reloadAfterError(videoDataUrl, currentTime, wasPlaying);
        } else {
          this.setSource(videoDataUrl);
        }
      });
    }
  }

  private setSource(url: string): void {
    this.primaryUrl = url;
    this.previewSeekDone = false;
  }

  /**
   * Recover from a media error (typically an expired signed URL) by swapping in
   * a fresh one. Only resume playback if the user actually had it playing --
   * the old code called play() unconditionally, which started an invisible
   * video behind the thumbnail. currentTime also has to wait for the new
   * source's metadata, since assigning src resets the element asynchronously.
   */
  private reloadAfterError(videoDataUrl: string, currentTime: number, wasPlaying: boolean): void {
    const media = this.media;
    if (!media) {
      return;
    }
    this.setSource(videoDataUrl);
    media.src = videoDataUrl;
    const restore = () => {
      media.removeEventListener('loadedmetadata', restore);
      if (currentTime > 0 && isFinite(currentTime) && currentTime < media.duration) {
        media.currentTime = currentTime;
      }
      if (wasPlaying) {
        this.safePlay();
      }
    };
    media.addEventListener('loadedmetadata', restore);
    media.load();
  }

  onTimeUpdateFunc(currentTime) {
    if(this.isTimeUpdate) {
      this.onTimeUpdate.emit(currentTime);
    }
  }

  addVideoComment() {
    if (this.isTimeUpdate) {
      this.safePause();
      this.onAddVideoComment.emit();
    }
  }

  downloadAssetFunc(assetUrl) {
    if (this.isTimeUpdate) {
      this.downloadAsset.emit(assetUrl);
    }
  }

  /**
   * Callers hide this viewer with [hidden], so the element survives teardown
   * of its container and would keep streaming. Detach the source to stop it.
   */
  ngOnDestroy(): void {
    const media = this.media;
    if (!media) {
      return;
    }
    media.removeEventListener('loadedmetadata', this.onLoadedMetadata);
    media.pause();
    media.removeAttribute('src');
    media.load();
  }
}
